import React, { useEffect, useMemo } from 'react';
import { Pagination } from 'pureUi/Pagination';
import { LoadingBar, ErrorBar } from 'ui/NetworkStatusBar';
import { usePagination } from 'hooks/usePagination';
import { ESortOrder } from 'store/common/types';
import { useInvoiceUploadsQuery } from './hooks/useInvoiceUploadsQuery';
import { InvoiceUploadsTable } from './components/InvoiceUploadsTable';
import { TInvoiceUploadTab } from './types';

interface IInvoiceUploadsTabProps {
  tab: TInvoiceUploadTab;
  headerLabel: string;
}

const getResultsText = (count: number): string => {
  if (!count) return '(no results found)';
  if (count === 1) return '(1 result found)';
  return `(${count} results found)`;
};

export const InvoiceUploadsTab: React.FC<IInvoiceUploadsTabProps> = ({ tab, headerLabel }) => {
  const pagination = usePagination({ initialPage: 1, initialPerPage: 10, initialTotal: 0 });

  const queryParams = useMemo(
    () => ({
      tab,
      offset: (pagination.page - 1) * pagination.perPage,
      limit: pagination.perPage,
    }),
    [tab, pagination.page, pagination.perPage]
  );

  const { data, isLoading, isError, isFetching } = useInvoiceUploadsQuery(queryParams);

  const total = data?.count ?? 0;
  useEffect(() => {
    if (data?.count !== undefined) {
      pagination.setTotal(data.count);
    }
  }, [data?.count]);

  if (isError) return <ErrorBar />;
  if (isLoading && !data) return <LoadingBar />;

  return (
    <div className="w-full">
      <div className="font-pt-sans text-[17px] text-black mb-2">
        <span className="font-bold">{headerLabel}</span> {getResultsText(total)}
      </div>

      <InvoiceUploadsTable
        tab={tab}
        rows={data?.imports ?? []}
        loading={isFetching}
        sortOrder={ESortOrder.DESC}
      />

      {total > 0 && (
        <Pagination
          className="mt-5"
          currentPage={pagination.page}
          itemsPerPage={pagination.perPage}
          onItemsPerPageChange={perPage => {
            pagination.setPerPage(perPage);
            pagination.setPage(1);
          }}
          onPageSelect={pagination.setPage}
          pageCount={pagination.pageCount}
        />
      )}
    </div>
  );
};
